/**
 * Read-modify-write helpers for the release policy (`GET|PUT /admin/v1/releases/policy`). The view
 * may be a little stale, so each change reads the policy fresh and changes only what it names.
 */

import type { ReleasePolicy, ReleaseProduct, ReleaseProductPolicy, ReleasesOverview } from '@aioe/shared';

import { routes } from '../api/client.ts';

/** Apply the same patch to each named product's policy. */
export async function patchProducts(view: ReleasesOverview, products: ReleaseProduct[], patch: Partial<ReleaseProductPolicy>): Promise<void> {
  const policy = await routes.releasePolicy();
  const next: ReleasePolicy['products'] = { ...policy.products };
  for (const product of products) {
    const base = policy.products[product] ?? view.products.find((p) => p.product === product)?.policy;
    if (base) next[product] = { ...base, ...patch };
  }
  await routes.saveReleasePolicy({ ...policy, products: next });
}

/** Change the rings, keeping every product's policy as it is. */
export async function patchRings(change: (rings: ReleasePolicy['rings']) => ReleasePolicy['rings']): Promise<void> {
  const policy = await routes.releasePolicy();
  await routes.saveReleasePolicy({ ...policy, rings: change(policy.rings) });
}

/** "1.2 GB", "450 MB", "80 kB". */
export function byteSize(n: number): string {
  return n < 1_000_000 ? `${Math.round(n / 1000)} kB` : n < 1_000_000_000 ? `${(n / 1_000_000).toFixed(0)} MB` : `${(n / 1_000_000_000).toFixed(1)} GB`;
}

export const PRODUCT_NAME: Record<ReleaseProduct, string> = { workbench: 'AI Workbench', node: 'Nodes', mesh: 'Mesh agent', aioe: 'AIOE platform' };

/** Whether this platform has storage for installers: with storage every product reports its held bytes (0 when none). */
export const hasInstallerStorage = (view: ReleasesOverview) => view.products.some((p) => p.mirroredBytes !== undefined);
